<template>
  <article v-if="data" class="flex flex-col">
    <div class="mb-[120px] flex gap-[20px]">
      <div class="flex flex-col xl:w-3/4 2xl:w-4/5">
        <BaseTheContentBreadcrumbs :current-page-title="getFullName" />
        <h1
          v-if="isOldBiographyFormat"
          class="mb-[28px] text-[32px] font-medium leading-[38px]"
          v-text="getFullName"
        />
        <CommonBlocksImageArticleBlock
          v-if="isOldBiographyFormat && data?.image"
          class="mb-[32px]"
          :published-at="data?.published_at"
          :src="data.image.src"
          :data="data"
        />
        <EditorParser v-if="data?.json_body" :json-str="data.json_body">
          <EditorMainImage
            :image-src="getSrc"
            :title="getFullName"
            :announce="data.announce"
            :source="data.cover_source"
            :description="data.cover_description"
            :time="data.time"
          />
        </EditorParser>
        <div
          v-else
          ref="pageBodyRef"
          class="page-body mb-[65px]"
          v-html="data.description"
        ></div>

        <!-- <CommonMobileBannerBlock /> -->
        <CommonBlocksNewTagsBlock class="mb-[80px]" v-bind="{ tags }" />

        <CommonSocialsSocialRedesign
          v-if="data.id"
          :id="data.id"
          :model-type="data.model_type ?? 'biography'"
          :views="data.views"
          class="mb-[80px]"
        />

        <div v-if="data?.authors?.length" class="flex flex-col">
          <h3 class="mb-[36px] text-[24px] font-medium">Автор</h3>
          <div class="grid grid-flow-row gap-5">
            <CommonAuthorInfo
              v-for="author in data.authors"
              :key="author.id"
              :author="author"
            />
          </div>
        </div>
      </div>
      <CommonBlocksRightArticleBlock class="xl:w-1/4 2xl:w-1/5" />
    </div>
    <CommonBlocksPageFooterBlocks />
  </article>
</template>

<script setup>
definePageMeta({
  layout: 'marginless',
});

const route = useRoute();
const slug = route?.params?.slug;
const { biographiesSlug } = useBiographiesBySlug(slug);
useErrorHandler(() => biographiesSlug.failureReason.value?.status);

const { isDesktop, isPad, isMobile } = useBreakpoint();

const data = computed(() => biographiesSlug?.data?.value);
const tags = computed(() => data.value?.tags);

const robots = ref('all');
const pageBodyRef = ref(null);

const isOldBiographyFormat = computed(() => !data.value?.json_body);

const getSrc = computed(() => {
  const src =
    data.value?.cover_image ||
    (isDesktop.value && data.value?.image?.desktop) ||
    (isPad.value && data.value?.image?.desktop) ||
    (isMobile.value && data.value?.image?.mobile) ||
    data.value?.image?.src;
  return src;
});

watch(pageBodyRef, (value) => {
  if (!value) return;
  renderImageFooter(value);
});

const seo = computed(() => data.value?.seo);
if (seo.value) {
  const arr = [];
  if (seo.value.index === false) arr.push('noindex');
  if (seo.value.follow === false) arr.push('nofollow');
  if (seo.value.archive === false) arr.push('noarchive');
  if (arr.length > 0) {
    robots.value = arr.reduce((previousValue, currentValue) => {
      return `${previousValue}, ${currentValue}`;
    });
  }
}

const getFullName = computed(() => {
  return [data.value?.surname, data.value?.firstname]
    .filter((el) => el)
    .join(' ');
});

useHead(
  computed(() => ({
    title: `${getFullName.value} | Читать биографии известных личностей РФ для школьников и студентов`,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: `Биографии великих личностей Российской Федерации. ${
          seo.value?.metadescription ?? getFullName.value
        }. Читать биографии известных личностей РФ для школьников и студентов на сайте Федерального портала истории России..`,
      },
      {
        'http-equiv': 'Last-Modified',
        content: new Date(seo.value?.last_modified).toUTCString(),
      },
      {
        hid: 'robots',
        name: 'robots',
        content: robots.value,
      },
      {
        hid: 'og:image',
        property: 'og:image',
        content: `${useHostApp()}${data.value?.image?.src}` || '',
      },
      {
        hid: 'og:type',
        property: 'og:type',
        content: 'article',
      },
      {
        property: 'og:title',
        content: `${getFullName.value} | Читать биографии известных личностей РФ для школьников и студентов`,
      },
      {
        property: 'og:url',
        content: `${useHostApp()}/read/biographies/${slug}`,
      },
      {
        property: 'og:description',
        content: `Биографии великих личностей Российской Федерации. ${
          seo.value?.metadescription ?? getFullName.value
        }. Читать биографии известных личностей РФ для школьников и студентов на сайте Федерального портала истории России..`,
      },
    ],
    link: [
      {
        rel: 'canonical',
        href: `${useHostApp()}${route.path}`,
      },
    ],
  })),
);

useSchemaOrg(() =>
  defineArticle({
    datePublished: new Date(data.value?.published_at),
    image: {
      url: `${useHostApp()}${getSrc.value}`,
      width: 800,
      height: 600,
      caption: data.value?.image?.alt,
    },
    about: {
      '@type': 'Person',
      name: getFullName.value,
    },
  }),
);
</script>
